import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { centsToMajorString, majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface CashRegister {
  id: string;
  branch_id: string;
  name: string;
  status: 'active' | 'inactive';
}

interface CashierUser {
  id: string;
  email: string;
  full_name: string;
}

interface OpenShiftDialogProps {
  registers: ReadonlyArray<CashRegister>;
  cashiers: ReadonlyArray<CashierUser>;
  submitting: boolean;
  on_close: () => void;
  on_submit: (input: {
    cash_register_id: string;
    cashier_id: string;
    initial_fund_cents: string;
    notes: string;
  }) => void | Promise<void>;
}

export function OpenShiftDialog({
  registers,
  cashiers,
  submitting,
  on_close,
  on_submit,
}: OpenShiftDialogProps): JSX.Element {
  const { t } = useTranslation();
  const default_register = registers[0]?.id ?? '';
  const default_cashier = cashiers[0]?.id ?? '';

  const [register_id, setRegisterId] = useState(default_register);
  const [cashier_id, setCashierId] = useState(default_cashier);
  const [amount_major, setAmountMajor] = useState('0.00');
  const [notes, setNotes] = useState('');

  const initial_fund_cents = useMemo(() => {
    try {
      return majorStringToCents(amount_major || '0').toString();
    } catch {
      return '';
    }
  }, [amount_major]);

  const can_submit =
    register_id.length > 0 &&
    cashier_id.length > 0 &&
    initial_fund_cents.length > 0 &&
    !submitting;

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    await on_submit({
      cash_register_id: register_id,
      cashier_id,
      initial_fund_cents,
      notes,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('cash_shifts.open_dialog_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="open-register">{t('cash_shifts.register')}</Label>
            <select
              id="open-register"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={register_id}
              onChange={(e) => setRegisterId(e.target.value)}
              disabled={submitting}
            >
              <option value="">{t('cash_shifts.pick_register')}</option>
              {registers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="open-cashier">{t('cash_shifts.cashier')}</Label>
            <select
              id="open-cashier"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={cashier_id}
              onChange={(e) => setCashierId(e.target.value)}
              disabled={submitting}
            >
              <option value="">{t('cash_shifts.pick_cashier')}</option>
              {cashiers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.full_name} · {c.email}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="open-fund">{t('cash_shifts.initial_fund')}</Label>
            <Input
              id="open-fund"
              inputMode="decimal"
              className="font-mono"
              value={amount_major}
              onChange={(e) => setAmountMajor(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              {t('cash_shifts.initial_fund_hint', {
                value: centsToMajorString(initial_fund_cents || '0'),
              })}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="open-notes">{t('cash_shifts.notes')}</Label>
            <textarea
              id="open-notes"
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {submitting ? t('common.loading') : t('cash_shifts.open_shift')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
